import React, { useMemo, useState } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router';
import classNames from 'classnames';
import { ButtonItem, PopupMenuGroup, Section } from '@atlaskit/menu';
import { PrimaryButton, PrimaryDropdownButton, useOverflowStatus } from '@atlaskit/atlassian-navigation';
import Popup, { TriggerProps } from '@atlaskit/popup';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import Tooltip from '@atlaskit/tooltip';
import { WarningDialog, LEFT_SIDEBAR } from '@css-xp/base';
import {
  DEFAULT_PAGE_ROUTE,
  customRouteRedirectPath,
  routeListViewFragments,
  routePaths,
  routeWithNoListViewFragments,
} from '@css-xp/ppc-constants';
import type { PartnerAccount, PartnerAccountBillTo } from '@css-xp/ppc-types';
import { getRouteUrl } from '@css-xp/ppc-utils';
import './transaction-account-switcher.scss';

interface SelectOption {
  heading: string;
  description: string;
  label: string;
  value: string;
}

export const TransactionAccountSwitcher = ({
  partnerAccounts,
  selectedPartnerAccount,
  setSelectedPartnerAccount,
  hasSelectInterface,
}: {
  selectedPartnerAccount: string;
  setSelectedPartnerAccount: (_val: string) => void;
  partnerAccounts: PartnerAccount[];
  hasSelectInterface?: boolean;
}) => {
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [selectedPartnerTxa, setSelectedPartnerTxa] = useState<string>(selectedPartnerAccount);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const overFlowStatus = useOverflowStatus();

  const history = useHistory();
  const location = useLocation();
  const getCommaSuffixedString = (value?: string | null) => {
    return value ? value + ', ' : '';
  };

  const getReadableAddressString = (addressDetails?: PartnerAccountBillTo) => {
    return (
      getCommaSuffixedString(addressDetails?.city) +
      getCommaSuffixedString(addressDetails?.state) +
      addressDetails?.country
    );
  };

  const transactionAccountOptions = useMemo<SelectOption[]>(
    () =>
      partnerAccounts.map((partnerAccount: PartnerAccount) => {
        const partnerAccountDetails = getReadableAddressString(partnerAccount.billTo);
        return {
          label: `${partnerAccount?.name}, ${partnerAccountDetails}`,
          value: partnerAccount.transactionAccountId,
          heading: partnerAccount?.name,
          description: partnerAccountDetails,
          isOptionDisabled: partnerAccount.transactionAccountId === selectedPartnerAccount,
        };
      }),
    [partnerAccounts]
  );

  const getRoutesList = (routePath: string, route: string) => {
    const defaultRouteTo = routePath.endsWith('/') ? routePath.slice(0, -1) : routePath;
    // split adds an empty string in the array by default so to skip the confusion using filter
    // All the base urls have back slash at the end so need to split including back slash
    return defaultRouteTo.split(route).filter(Boolean);
  };

  const getRedirectParams = () => {
    let isEntityDetailsPage = false;
    let routeToRedirectUrl = Object.values(routePaths).find((routePath) =>
      matchPath(location.pathname, {
        path: routePath,
        exact: true,
        strict: false,
      })
    );

    if (routeToRedirectUrl && customRouteRedirectPath[routeToRedirectUrl]) {
      routeToRedirectUrl = customRouteRedirectPath[routeToRedirectUrl];
      return {
        isEntityDetailsPage: true,
        routeToRedirectUrl,
      };
    }

    // pages with entity details screen, splitting the route fragments to remove url method ids after that route fragment
    Object.values(routeListViewFragments).filter((item) => {
      if (routeToRedirectUrl?.includes(item)) {
        const currentRouteList = getRoutesList(routeToRedirectUrl, item);
        if (currentRouteList.length >= 2) {
          routeToRedirectUrl = currentRouteList[0] + item;
          isEntityDetailsPage = true;
        }
      }
    });

    // pages with no list view pages should be redirected to home page by default on txa switch
    Object.values(routeWithNoListViewFragments).filter((item) => {
      if (routeToRedirectUrl?.includes(item)) {
        const currentRouteList = getRoutesList(routeToRedirectUrl, item);
        // kept this logic separately since fragment and default urls are different
        if (currentRouteList.length >= 2) {
          routeToRedirectUrl = DEFAULT_PAGE_ROUTE;
          isEntityDetailsPage = true;
        }
      }
    });

    return {
      isEntityDetailsPage,
      routeToRedirectUrl,
    };
  };

  const renderSingleTxaAccount = () => {
    return overFlowStatus.isVisible ? (
      <Tooltip content={findTransactionAccount(selectedPartnerAccount)?.heading} position="bottom-start">
        <PrimaryButton
          testId="txa-redirection-btn"
          onClick={() => redirectToRoute(selectedPartnerTxa, routePaths.details)}
          className="single-txa-btn"
        >
          {findTransactionAccount(selectedPartnerAccount)?.heading}
        </PrimaryButton>
      </Tooltip>
    ) : (
      <div className="single-txa-btn-collapsed">
        <ButtonItem
          testId="txa-redirection-btn"
          onClick={() => redirectToRoute(selectedPartnerTxa, routePaths.details)}
        >
          <span className="collapsed-btn-text">{findTransactionAccount(selectedPartnerAccount)?.heading}</span>
        </ButtonItem>
      </div>
    );
  };

  const renderMultipleTxaAccounts = () => {
    return (
      <Popup
        testId="txa-switcher-navigation-popup"
        id={hasSelectInterface ? 'txa-switcher-navigation-popup-v2' : ''}
        content={renderTxaSwitcher}
        isOpen={isOpen}
        placement={overFlowStatus.isVisible ? 'bottom-start' : 'right-start'}
        onClose={() => setIsOpen(false)}
        trigger={(triggerProps: TriggerProps) =>
          overFlowStatus.isVisible ? (
            <PrimaryDropdownButton
              {...triggerProps}
              testId="navigation-dropdown-btn"
              isSelected={isOpen}
              onClick={() => setIsOpen((prev) => !prev)}
              className="primary-dropdown-btn"
            >
              {findTransactionAccount(selectedPartnerAccount)?.heading}
            </PrimaryDropdownButton>
          ) : (
            <div className="collapsed-btn-item">
              <ButtonItem
                {...triggerProps}
                isSelected
                onClick={() => setIsOpen((prev) => !prev)}
                iconAfter={
                  <span className="chevron-right-icon">
                    <ChevronRightIcon label="" />
                  </span>
                }
                testId="right-menu-popup-btn"
              >
                <span className="collapsed-btn-text">{findTransactionAccount(selectedPartnerAccount)?.heading}</span>
              </ButtonItem>
            </div>
          )
        }
      />
    );
  };

  const redirectToRoute = (txaId: string, urlToRedirect?: string) => {
    history.push(
      getRouteUrl(urlToRedirect || DEFAULT_PAGE_ROUTE, {
        txaId: txaId,
      })
    );
  };

  const onTxaChange = (option: SelectOption) => {
    const redirectParams = getRedirectParams();
    if (redirectParams.isEntityDetailsPage) {
      setSelectedPartnerTxa(option.value);
      setConfirmDialogOpen(true);
    } else {
      setSelectedPartnerAccount(option.value);
      redirectToRoute(option.value, redirectParams.routeToRedirectUrl);
    }
  };

  const handleConfirmDialog = () => {
    const redirectParams = getRedirectParams();
    setConfirmDialogOpen(false);
    setSelectedPartnerAccount(selectedPartnerTxa);
    redirectToRoute(selectedPartnerTxa, redirectParams.routeToRedirectUrl);
  };

  const findTransactionAccount = (value: string) => {
    return transactionAccountOptions.find((transactionAccount) => transactionAccount.value === value);
  };

  const renderTxaSwitcher = () => {
    const txaId = findTransactionAccount(selectedPartnerAccount);
    const horizontalPaddingInSidebar = 16;
    const sidebarPopupWidth = LEFT_SIDEBAR.WIDTH - horizontalPaddingInSidebar - 2; // Extra '2' for keeping it smaller than the trigger button

    return (
      <PopupMenuGroup
        maxHeight={300}
        maxWidth={hasSelectInterface ? sidebarPopupWidth : 360}
        {...(hasSelectInterface && { minWidth: sidebarPopupWidth })}
      >
        {hasSelectInterface || (
          <Section title="Current">
            <div className="current-txa-account">
              <ButtonItem
                testId="currect-txa-account"
                description={
                  <span className="txa-btn-description">
                    {findTransactionAccount(selectedPartnerAccount)?.description}{' '}
                  </span>
                }
                onClick={() => {
                  if (txaId) {
                    onTxaChange(txaId);
                  }
                }}
              >
                <span className="txa-btn-heading">{findTransactionAccount(selectedPartnerAccount)?.heading}</span>
              </ButtonItem>
            </div>

            <ButtonItem
              testId="manage-txa-account"
              onClick={() => (redirectToRoute(selectedPartnerAccount, routePaths.details), setIsOpen(false))}
            >
              View details
            </ButtonItem>
          </Section>
        )}
        <Section
          hasSeparator={!hasSelectInterface}
          title={hasSelectInterface ? undefined : 'Switch billing account'}
          isScrollable
        >
          {partnerAccounts.map((partnerAccount: PartnerAccount) => {
            if (partnerAccount.transactionAccountId !== selectedPartnerAccount) {
              const partnerAccountDetails = getReadableAddressString(partnerAccount.billTo);
              const transactionAccount = findTransactionAccount(partnerAccount.transactionAccountId);
              return (
                // eslint-disable-next-line react/jsx-key
                <ButtonItem
                  description={
                    hasSelectInterface ? undefined : (
                      <span className="txa-btn-description">{partnerAccountDetails} </span>
                    )
                  }
                  onClick={() => {
                    setIsOpen(false);
                    if (transactionAccount) {
                      onTxaChange(transactionAccount);
                    }
                  }}
                >
                  <span className="txa-btn-heading">{partnerAccount.name}</span>
                </ButtonItem>
              );
            }
          })}
        </Section>
      </PopupMenuGroup>
    );
  };

  const switcherClassName = classNames('transaction-account-switch-wrapper', {
    'transaction-account-switch-wrapper-v2': hasSelectInterface,
  });

  return (
    <div className={switcherClassName} data-testid="transaction-account-switch-wrapper">
      <div className="txa-switch-container">
        {partnerAccounts.length === 1 && renderSingleTxaAccount()}
        {partnerAccounts.length > 1 && renderMultipleTxaAccounts()}
      </div>
      <WarningDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={() => handleConfirmDialog()}
        header={'Switch billing accounts?'}
        description={
          <p>
            If you're currently viewing information that isn't in the billing account you want switch to, you'll be
            redirected to a default list page.
          </p>
        }
        okLabel={'Yes, switch'}
        testId={'txa-switch-warning'}
      />
      {!hasSelectInterface && overFlowStatus.isVisible && <div className="txa-switcher-vertical-divider" />}
    </div>
  );
};
